import { IntlShape } from 'react-intl';
import { Sentry } from '../../../errorBoundary/sentry';
import { catalogSongMessages } from './catalogSongMessages';
import { MasterPartError } from './masterUploadParts';
import { masterUploadRefusal } from './masterUploadRefusal';

/** The step of the master upload that failed. */
export type MasterUploadStage = 'create' | 'parts' | 'complete';

/**
 * The sentence for a failed master upload. An API refusal keeps the sentence of the server. A
 * part that did not reach S3 says why: the browser blocked it, S3 refused it, or the browser
 * cannot read the file. Any other failure gets the general sentence.
 */
export function masterUploadErrorMessage(intl: IntlShape, failure: unknown): string {
  if (failure instanceof MasterPartError) {
    switch (failure.kind) {
      case 'unreadable':
        return intl.formatMessage(catalogSongMessages.fileUnreadable);
      case 'refused':
        return failure.status > 0
          ? intl.formatMessage(catalogSongMessages.uploadRefused, { status: failure.status })
          : intl.formatMessage(catalogSongMessages.uploadFailed);
      case 'network':
        return intl.formatMessage(catalogSongMessages.uploadBlocked);
    }
  }
  return masterUploadRefusal(failure).message ?? intl.formatMessage(catalogSongMessages.uploadFailed);
}

function storageHost(url: string | undefined): string | undefined {
  if (url === undefined) {
    return undefined;
  }
  try {
    return new URL(url).host;
  } catch {
    return undefined;
  }
}

/**
 * Logs a failed master upload and sends it to Sentry with what we know (never the presigned
 * address: it is a credential). An API refusal with a server message (a 4xx the user can fix) is
 * not a bug, so it goes only to the console.
 */
export function reportMasterUploadError(
  stage: MasterUploadStage,
  failure: unknown,
  file: Pick<File, 'name' | 'type' | 'size'>,
  partUrl?: string
): void {
  console.error(`Master upload: ${stage} failed`, failure);
  const { message, reason } = masterUploadRefusal(failure);
  const statusCode = (failure as { statusCode?: unknown } | null)?.statusCode;
  const isUserFixable = typeof statusCode === 'number' && statusCode < 500 && message !== undefined;
  if (!(failure instanceof MasterPartError) && isUserFixable) {
    return;
  }
  const part = failure instanceof MasterPartError ? failure : undefined;
  Sentry.captureException(failure, {
    tags: {
      masterUploadStage: stage,
      masterUploadFailure: part?.kind ?? 'api',
    },
    extra: {
      partNumber: part?.partNumber,
      status: part?.status,
      reason,
      fileExtension: file.name.split('.').pop()?.toLowerCase(),
      fileType: file.type,
      fileSize: file.size,
      storageHost: storageHost(partUrl),
      online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
    },
  });
}
